import {
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { ArrowRight, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/shared/PageHeader';
import { spectralWorkspaceStore } from '../../features/research/store/spectralWorkspaceStore';
import type { SpectralHubListItem } from '../../features/research/store/spectralWorkspaceStore';
import { useAuthUser } from '../../providers/AuthProvider';

const formatDate = (value?: string | null) => {
  if (!value) return 'n/a';
  return new Date(value).toLocaleString('it-IT');
};

export function SpectralAnalysisHubPage() {
  const navigate = useNavigate();
  const user = useAuthUser();
  const [opened, { open, close }] = useDisclosure(false);
  const [analyses, setAnalyses] = useState<SpectralHubListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SpectralHubListItem | null>(
    null
  );
  const [deleteOpened, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);

  const loadAnalyses = () => {
    setIsLoading(true);
    spectralWorkspaceStore
      .listAnalysesForHub()
      .then(setAnalyses)
      .catch(error => {
        notifications.show({
          color: 'red',
          title: 'Errore caricamento analisi',
          message:
            error instanceof Error
              ? error.message
              : 'Impossibile recuperare le analisi salvate.',
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    loadAnalyses();
  }, []);

  const handleClose = () => {
    setName('');
    setDescription('');
    close();
  };

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      notifications.show({
        color: 'red',
        title: 'Nome richiesto',
        message: 'Inserisci un nome per l’analisi.',
      });
      return;
    }

    setIsCreating(true);

    try {
      const snapshot = await spectralWorkspaceStore.createAnalysis({
        name: trimmedName,
        description: description.trim() || undefined,
        author: user?.email || user?.name || 'Utente',
      });
      handleClose();
      navigate(`/research/spectral-normalization/${snapshot.analysisId}`);
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Errore creazione analisi',
        message:
          error instanceof Error
            ? error.message
            : 'Impossibile creare l’analisi.',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenDelete = (analysis: SpectralHubListItem) => {
    setDeleteTarget(analysis);
    openDeleteModal();
  };

  const handleCloseDelete = () => {
    setDeleteTarget(null);
    closeDeleteModal();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setDeletingId(deleteTarget.analysisId);

    try {
      await spectralWorkspaceStore.deleteAnalysis(deleteTarget.analysisId);
      setAnalyses(current =>
        current.filter(item => item.analysisId !== deleteTarget.analysisId)
      );
      handleCloseDelete();
      notifications.show({
        color: 'teal',
        title: 'Analisi eliminata',
        message: `"${deleteTarget.name}" è stata rimossa.`,
      });
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Eliminazione non riuscita',
        message:
          error instanceof Error
            ? error.message
            : 'Impossibile eliminare l’analisi.',
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <PageHeader
        title='NORMALIZZAZIONE SPETTRALE'
        subtitle='Workspace per confronto temporale di sessioni completed su dati HR effettivi.'
        breadcrumbs={[
          { label: 'Ricerca', href: '/research' },
          { label: 'Normalizzazione spettrale' },
        ]}
        backUrl='/research'
        actions={
          <Group gap='xs'>
            <Button
              variant='light'
              onClick={() =>
                navigate('/research/spectral-normalization/documentation')
              }
            >
              Documentazione
            </Button>
            <Button leftSection={<Plus size={16} />} onClick={open}>
              Nuova analisi
            </Button>
          </Group>
        }
      />

      <Divider my='md' />

      <Stack gap='md'>
        <Card withBorder>
          <Group justify='space-between'>
            <div>
              <Title order={4}>Analisi salvate</Title>
              <Text c='dimmed' size='sm'>
                Apri un’analisi esistente o creane una nuova con nome e
                descrizione.
              </Text>
            </div>
            <Badge variant='light'>{analyses.length} analisi</Badge>
          </Group>
        </Card>

        <Card withBorder padding={0}>
          {isLoading ? (
            <Group justify='center' py='xl'>
              <Loader size='sm' />
            </Group>
          ) : analyses.length === 0 ? (
            <Text c='dimmed' p='md'>
              Nessuna analisi ancora creata. Usa Nuova analisi per iniziare.
            </Text>
          ) : (
            <ScrollArea>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Nome</Table.Th>
                    <Table.Th>Tipo</Table.Th>
                    <Table.Th>Descrizione</Table.Th>
                    <Table.Th ta='right'>Sessioni</Table.Th>
                    <Table.Th>Aggiornata</Table.Th>
                    <Table.Th ta='right'>Azioni</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {analyses.map(analysis => (
                    <Table.Tr key={analysis.analysisId}>
                      <Table.Td fw={600}>{analysis.name}</Table.Td>
                      <Table.Td>
                        <Badge
                          variant='light'
                          color={analysis.kind === 'legacy' ? 'gray' : 'blue'}
                        >
                          {analysis.kind === 'legacy' ? 'Legacy' : 'Workspace'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size='sm' c='dimmed' lineClamp={2}>
                          {analysis.description || '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td ta='right'>{analysis.sessionCount}</Table.Td>
                      <Table.Td>{formatDate(analysis.updatedAt)}</Table.Td>
                      <Table.Td ta='right'>
                        <Group gap='xs' justify='flex-end'>
                          <Button
                            variant='light'
                            color='red'
                            size='compact-sm'
                            leftSection={<Trash2 size={14} />}
                            loading={deletingId === analysis.analysisId}
                            onClick={() => handleOpenDelete(analysis)}
                          >
                            Elimina
                          </Button>
                          <Button
                            variant='light'
                            size='compact-sm'
                            rightSection={<ArrowRight size={14} />}
                            onClick={() =>
                              navigate(
                                analysis.kind === 'legacy'
                                  ? `/research/spectral-normalization/legacy/${analysis.analysisId}`
                                  : `/research/spectral-normalization/${analysis.analysisId}`
                              )
                            }
                          >
                            Apri
                          </Button>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          )}
        </Card>
      </Stack>

      <Modal
        opened={opened}
        onClose={handleClose}
        title='Nuova analisi'
        centered
      >
        <Stack gap='md'>
          <TextInput
            label='Nome analisi'
            value={name}
            onChange={event => setName(event.currentTarget.value)}
            placeholder='Es. Trial BECOME - confronto gruppo'
          />
          <Textarea
            label='Descrizione'
            value={description}
            onChange={event => setDescription(event.currentTarget.value)}
            placeholder='Descrizione opzionale'
            autosize
            minRows={2}
          />
          <Group justify='flex-end'>
            <Button variant='subtle' onClick={handleClose}>
              Annulla
            </Button>
            <Button loading={isCreating} onClick={handleCreate}>
              Crea e apri
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={deleteOpened}
        onClose={handleCloseDelete}
        title='Elimina analisi'
        centered
      >
        <Stack gap='md'>
          <Text size='sm'>
            Vuoi eliminare definitivamente l’analisi{' '}
            <Text span fw={600}>
              {deleteTarget?.name}
            </Text>
            ? L’operazione non è reversibile.
          </Text>
          <Group justify='flex-end'>
            <Button variant='subtle' onClick={handleCloseDelete}>
              Annulla
            </Button>
            <Button
              color='red'
              loading={Boolean(deletingId)}
              onClick={handleDelete}
            >
              Elimina
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export default SpectralAnalysisHubPage;
